import { Box, Heading, Separator, Table, Text } from "@chakra-ui/react-v3";
import { ISO_DATE } from "@metriport/shared/common/date";
import dayjs from "dayjs";
import { ResourceAnalysis } from "../components/resource-analysis";
import { DocumentsSection } from "../components/side-panel-sections/documents-section";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { SocialHistoryRowData } from "../tables/social-history";
import {
  getDateForResourceType,
  getValueForResourceType,
} from "../utils/resource-value-extractors";

export type SocialHistorySidePanelContentProps = {
  data: SocialHistoryRowData;
};

export function SocialHistorySidePanelContent({ data }: SocialHistorySidePanelContentProps) {
  const series = data.originalData;

  const tableData = series.resources
    .map((resource, index) => {
      const date = getDateForResourceType(resource);
      const value = getValueForResourceType(resource);
      return {
        id: `resource-${index}`,
        date: date && date !== "-" ? dayjs(date).format(ISO_DATE) : "-",
        value: value?.toString() || "-",
        rawDate: date,
      };
    })
    .filter(item => item.rawDate && item.rawDate !== "-")
    .sort((a, b) => dayjs(b.rawDate).diff(dayjs(a.rawDate)));

  return (
    <SidePanelLayout
      title="Social History"
      subtitle={data.observation}
      metadata={`${series.resources.length} entries • ${dayjs(series.earliestDate).format(
        ISO_DATE
      )} to ${dayjs(series.latestDate).format(ISO_DATE)}`}
    >
      <ResourceAnalysis />

      <Separator borderColor="border" />

      <Box>
        <Heading size="md" mb={4} color="fg">
          All Data Points ({tableData.length})
        </Heading>
        <Box
          borderWidth="1px"
          borderColor="border"
          borderRadius="md"
          overflow="hidden"
          bg="bg.subtle"
        >
          <Table.Root variant="line" size="sm">
            <Table.Header>
              <Table.Row bg="bg.panel">
                <Table.ColumnHeader fontWeight="bold" color="fg">
                  Date
                </Table.ColumnHeader>
                <Table.ColumnHeader fontWeight="bold" color="fg">
                  Value
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tableData.map(row => (
                <Table.Row key={row.id} bg="bg.subtle" borderColor="border">
                  <Table.Cell borderColor="border">
                    <Text fontSize="sm" color="fg">
                      {row.date}
                    </Text>
                  </Table.Cell>
                  <Table.Cell borderColor="border">
                    <Text fontSize="sm" color="fg">
                      {row.value}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        </Box>
      </Box>

      <Separator borderColor="border" />

      <DocumentsSection resources={series.resources} resourceType="Observation" />
    </SidePanelLayout>
  );
}
